import { useState, useEffect, useCallback } from 'react';
import {
  productService, estoqueService, fornecedorService,
  Product, Fornecedor, NotaEntrada, SugestaoCompra,
} from '../../services';
import { usePagination } from '../../hooks/usePagination';
import PaginationControls from '../../components/ui/PaginationControls';
import ModalConfirmacao from '../../components/ModalConfirmacao';

type Tab = 'notas' | 'inventario' | 'fornecedores';

const fmtDinheiro = (v: string | number) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtData = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export default function Estoque() {
  const [tab, setTab] = useState<Tab>('notas');
  const [products, setProducts] = useState<Product[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [notas, setNotas] = useState<NotaEntrada[]>([]);
  const [sugestoes, setSugestoes] = useState<SugestaoCompra[]>([]);
  // Notas crescem com o tempo — paginadas no backend
  const notasPg = usePagination({ defaultLimit: 20 });
  const [isLoading, setIsLoading] = useState(true);
  const [showNota, setShowNota] = useState(false);
  const [showFornecedor, setShowFornecedor] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Inventário: saldo contado por produto (default = saldo atual)
  const [contagem, setContagem] = useState<Record<string, string>>({});
  const [inventariando, setInventariando] = useState(false);

  // Form da nota
  const [notaForm, setNotaForm] = useState({
    fornecedor_id: '',
    numero: '',
    observacao: '',
    itens: [] as Array<{ produto_id: string; quantidade: string; preco_unitario: string }>,
  });
  // Form do fornecedor (cadastro na hora, dentro do modal da nota)
  const [novoFornecedor, setNovoFornecedor] = useState({ nome: '', cnpj: '', telefone: '' });
  const [mostrarNovoFornecedor, setMostrarNovoFornecedor] = useState(false);

  // T130: o estoque tem listagem PRÓPRIA (busca + paginação no servidor) — antes
  // eram os 100 primeiros produtos, e acima disso item sumia sem aviso
  const [controlados, setControlados] = useState<Product[]>([]);
  const [buscaEstoque, setBuscaEstoque] = useState('');
  const [paginaEstoque, setPaginaEstoque] = useState(1);
  const [totalEstoque, setTotalEstoque] = useState(0);
  const LIMITE_ESTOQUE = 20;

  const carregarControlados = useCallback(async () => {
    try {
      const r = await productService.findAll({
        busca: buscaEstoque.trim() || undefined,
        controlar_estoque: true,
        page: paginaEstoque,
        limit: LIMITE_ESTOQUE,
      });
      setControlados(r.data.data);
      setTotalEstoque(r.data.pagination?.total ?? r.data.data.length);
    } catch (error) {
      console.error('Erro ao carregar itens com estoque controlado:', error);
    }
  }, [buscaEstoque, paginaEstoque]);

  useEffect(() => {
    void carregarControlados();
  }, [carregarControlados]);

  const totalPaginasEstoque = Math.max(1, Math.ceil(totalEstoque / LIMITE_ESTOQUE));

  // T130: seletor de itens da nota busca no catálogo inteiro (por nome)
  const [buscaItemNota, setBuscaItemNota] = useState('');
  const [opcoesNota, setOpcoesNota] = useState<Product[]>([]);

  useEffect(() => {
    if (!showNota) return;
    let ativo = true;
    const timer = setTimeout(async () => {
      try {
        const r = await productService.findAll({
          busca: buscaItemNota.trim() || undefined,
          controlar_estoque: true,
          limit: 50,
        });
        if (ativo) setOpcoesNota(r.data.data);
      } catch {
        // mantém a lista anterior
      }
    }, 300);
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [showNota, buscaItemNota]);

  // O item já escolhido na nota nunca some do seletor por causa do filtro
  const opcoesSelectNota = (() => {
    const lista = [...(opcoesNota.length > 0 ? opcoesNota : controlados)];
    for (const item of notaForm.itens) {
      if (!item.produto_id || lista.some((p) => p.id === item.produto_id)) continue;
      const achado =
        controlados.find((p) => p.id === item.produto_id) ??
        products.find((p) => p.id === item.produto_id);
      if (achado) lista.unshift(achado);
    }
    return lista;
  })();

  const loadData = useCallback(async () => {
    try {
      const [prods, forn, notasResp, sugs] = await Promise.all([
        productService.findAll({ limit: 100 }),
        fornecedorService.listar(),
        estoqueService.notas({ page: notasPg.page, limit: notasPg.limit }),
        estoqueService.sugestoesCompra(),
      ]);
      setProducts(prods.data.data);
      setFornecedores(forn.data.data);
      setNotas(notasResp.data.data);
      notasPg.syncFromResponse(notasResp.data);
      setSugestoes(sugs.data.data);
    } catch (error) {
      console.error('Erro ao carregar estoque:', error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notasPg.page, notasPg.limit]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // ── Nota ────────────────────────────────────────────────────────────────

  const adicionarItemNota = (item?: { produto_id: string; quantidade: string }) =>
    setNotaForm((f) => ({
      ...f,
      itens: [...f.itens, { produto_id: item?.produto_id ?? '', quantidade: item?.quantidade ?? '', preco_unitario: '' }],
    }));

  const usarSugestao = (s: SugestaoCompra) => {
    if (!showNota) setShowNota(true);
    setNotaForm((f) => ({
      ...f,
      itens: [...f.itens, { produto_id: s.id, quantidade: String(s.sugestao || 1), preco_unitario: '' }],
    }));
  };

  const criarFornecedorNaHora = async () => {
    if (!novoFornecedor.nome.trim()) {
      alert('Informe o nome do fornecedor.');
      return;
    }
    try {
      const res = await fornecedorService.criar(novoFornecedor);
      setFornecedores((f) => [...f, res.data.data]);
      setNotaForm((f) => ({ ...f, fornecedor_id: res.data.data.id }));
      setNovoFornecedor({ nome: '', cnpj: '', telefone: '' });
      setMostrarNovoFornecedor(false);
    } catch (error) {
      alert((error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao cadastrar fornecedor');
    }
  };

  const salvarNota = async () => {
    if (!notaForm.fornecedor_id) {
      setErro('Selecione (ou cadastre) o fornecedor.');
      return;
    }
    const itens = notaForm.itens.filter((i) => i.produto_id);
    if (!itens.length) {
      setErro('Adicione pelo menos 1 produto à nota.');
      return;
    }
    const itensValidos = itens.map((i) => ({
      produto_id: i.produto_id,
      quantidade: Math.floor(Number(i.quantidade)),
      preco_unitario: Number(i.preco_unitario) || 0,
    }));
    if (itensValidos.some((i) => i.quantidade <= 0)) {
      setErro('Quantidade inválida em um dos itens.');
      return;
    }
    setErro('');
    setSalvando(true);
    try {
      await estoqueService.criarNota({
        fornecedor_id: notaForm.fornecedor_id,
        numero: notaForm.numero.trim() || undefined,
        observacao: notaForm.observacao.trim() || undefined,
        itens: itensValidos,
      });
      setShowNota(false);
      setNotaForm({ fornecedor_id: '', numero: '', observacao: '', itens: [] });
      await loadData();
    } catch (error) {
      setErro((error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao lançar a nota.');
    } finally {
      setSalvando(false);
    }
  };

  /** T128 — estorna a nota inteira: devolve o estoque e desfaz o custo médio */
  const estornarNota = async (nota: NotaEntrada) => {
    if (nota.estornada_em) return;

    const motivo = prompt(
      `Estornar a nota ${nota.numero ? `nº ${nota.numero}` : 'sem número'} de ${nota.fornecedor.nome}?\n\n` +
        'O estoque dos itens volta a diminuir e o custo médio é recalculado. ' +
        'Se o estoque já foi consumido, o sistema recusa e explica.\n\nMotivo do estorno:',
    );
    if (!motivo || motivo.trim().length < 3) return;

    try {
      await estoqueService.estornarNota(nota.id, motivo.trim());
      await loadData();
      await carregarControlados();
    } catch (error) {
      alert(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Não foi possível estornar a nota.',
      );
    }
  };

  // ── Inventário ──────────────────────────────────────────────────────────

  // T215: aplicar inventário usa o modal do sistema e o resultado aparece na
  // própria tela (antes: confirm + alert do navegador)
  const [inventarioParaConfirmar, setInventarioParaConfirmar] = useState<
    { produto: Product; contado: number }[] | null
  >(null);
  const [mensagemEstoque, setMensagemEstoque] = useState<
    { tipo: 'ok' | 'erro'; linhas: string[] } | null
  >(null);

  const aplicarInventario = () => {
    const mudancas = controlados
      .map((p) => ({ produto: p, contado: Number(contagem[p.id]) }))
      .filter((m) => !Number.isNaN(m.contado) && m.contado >= 0 && m.contado !== m.produto.estoque_atual);
    if (!mudancas.length) {
      setMensagemEstoque({
        tipo: 'erro',
        linhas: ['Nenhuma diferença para ajustar: confira os valores contados.'],
      });
      return;
    }
    setMensagemEstoque(null);
    setInventarioParaConfirmar(mudancas);
  };

  const confirmarInventario = async () => {
    if (!inventarioParaConfirmar) return;
    const mudancas = inventarioParaConfirmar;
    setInventariando(true);
    try {
      // T129: uma única chamada em transação — ou tudo entra, ou nada entra
      const r = await estoqueService.inventarioLote(
        mudancas.map((m) => ({
          produto_id: m.produto.id,
          saldo_contado: m.contado,
          saldo_anterior: m.produto.estoque_atual,
        })),
      );
      const { ajustados, semDiferenca, falhas, mudouDurante, total } = r.data.data;

      const linhas = [
        `Inventário aplicado: ${ajustados} de ${total} item(ns) ajustado(s).`,
        semDiferenca > 0 ? `${semDiferenca} sem diferença (nada a fazer).` : '',
        falhas.length > 0
          ? `Não ajustados:\n${falhas.map((f) => `• ${f.nome}: ${f.motivo}`).join('\n')}`
          : '',
        mudouDurante.length > 0
          ? `Atenção — o saldo mudou durante a contagem:\n${mudouDurante.map((m) => `• ${m}`).join('\n')}`
          : '',
      ].filter(Boolean);

      setMensagemEstoque({ tipo: 'ok', linhas });
      setContagem({});
      await loadData();
      await carregarControlados();
    } catch (error) {
      setMensagemEstoque({
        tipo: 'erro',
        linhas: [
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Erro ao aplicar inventário. Nada foi alterado.',
        ],
      });
    } finally {
      setInventariando(false);
      setInventarioParaConfirmar(null);
    }
  };

  // ── Fornecedores ────────────────────────────────────────────────────────

  const salvarFornecedor = async () => {
    if (!novoFornecedor.nome.trim()) {
      alert('Informe o nome do fornecedor.');
      return;
    }
    setSalvando(true);
    try {
      await fornecedorService.criar(novoFornecedor);
      setShowFornecedor(false);
      setNovoFornecedor({ nome: '', cnpj: '', telefone: '' });
      const res = await fornecedorService.listar();
      setFornecedores(res.data.data);
    } catch (error) {
      alert((error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao cadastrar fornecedor');
    } finally {
      setSalvando(false);
    }
  };

  // T215: exclusão de fornecedor no modal do sistema
  const [fornecedorParaExcluir, setFornecedorParaExcluir] = useState<Fornecedor | null>(null);

  const excluirFornecedor = (f: Fornecedor) => {
    setMensagemEstoque(null);
    setFornecedorParaExcluir(f);
  };

  const confirmarExclusaoFornecedor = async () => {
    const f = fornecedorParaExcluir;
    if (!f) return;
    try {
      await fornecedorService.excluir(f.id);
      setFornecedores((fs) => fs.filter((x) => x.id !== f.id));
    } catch (error) {
      setMensagemEstoque({
        tipo: 'erro',
        linhas: [
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Erro ao excluir fornecedor',
        ],
      });
    } finally {
      setFornecedorParaExcluir(null);
    }
  };

  const totalNota = (n: NotaEntrada) =>
    (n.itens ?? []).reduce((acc, i) => acc + Number(i.quantidade) * Number(i.preco_unitario || 0), 0);

  if (isLoading) {
    return <div className="text-gray-400 p-8">Carregando estoque...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-100">📦 Estoque</h1>
        {tab === 'notas' && (
          <button onClick={() => { setErro(''); setShowNota(true); }} className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-sm font-medium">
            + Lançar nota de entrada
          </button>
        )}
        {tab === 'fornecedores' && (
          <button onClick={() => setShowFornecedor(true)} className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-sm font-medium">
            + Cadastrar fornecedor
          </button>
        )}
      </div>

      {/* Sub-abas */}
      <div className="grid grid-cols-3 gap-1 mb-5 bg-gray-800/60 rounded-lg p-1 w-full sm:w-fit sm:flex sm:gap-1">
        {([
          ['notas', '📥 Notas', '📥 Notas de entrada'],
          ['inventario', '📊 Inventário', '📊 Inventário'],
          ['fornecedores', '🏢 Fornecedores', '🏢 Fornecedores'],
        ] as Array<[Tab, string, string]>).map(([id, short, full]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-1 sm:px-4 py-2 rounded-md text-[11px] sm:text-sm font-medium whitespace-nowrap transition-colors ${tab === id ? 'bg-primary-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
          >
            <span className="sm:hidden">{short}</span>
            <span className="hidden sm:inline">{full}</span>
          </button>
        ))}
      </div>

      {/* ── TAB: NOTAS DE ENTRADA ─────────────────────────────────────── */}
      {tab === 'notas' && (
        <div className="space-y-5">
          {/* Sugestão de compra */}
          {sugestoes.length > 0 && (
            <div className="bg-amber-950/40 border border-amber-700/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-300 mb-2">🛒 Lista de compras sugerida ({sugestoes.length})</h3>
              <p className="text-xs text-amber-200/70 mb-3">Produtos no/abaixo do estoque mínimo — clique para adicionar à nota.</p>
              <div className="space-y-1.5">
                {sugestoes.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => usarSugestao(s)}
                    className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 bg-amber-900/30 hover:bg-amber-900/50 rounded-md px-3 py-2 text-left transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-amber-100 truncate">{s.name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${s.estoque_atual === 0 ? 'bg-red-900/70 text-red-200' : 'bg-amber-800/70 text-amber-200'}`}>
                        falta {s.sugestao} {s.unidade}
                      </span>
                    </div>
                    <span className="text-xs text-amber-300 whitespace-nowrap">
                      saldo {s.estoque_atual} {s.unidade} / mín {s.estoque_minimo} = obter {s.sugestao} {s.unidade}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notas lançadas */}
          <div className="space-y-2">
            {notas.length === 0 && (
              <div className="bg-gray-800/50 rounded-lg p-8 text-center text-gray-500 text-sm">
                Nenhuma nota lançada ainda. Clique em “+ Lançar nota de entrada”.
              </div>
            )}
            {notas.map((n) => (
              <details key={n.id} className={`rounded-lg p-3 group ${n.estornada_em ? 'bg-red-950/30 border border-red-900/40' : 'bg-gray-800/50'}`}>
                <summary className="cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-sm">
                  <span className="text-gray-200 font-medium">
                    🧾 Nota {n.numero ? `nº ${n.numero}` : 'sem número'}{' '}
                    <span className="text-gray-400 font-normal">— {n.fornecedor.nome}</span>
                    {/* T128: selo de nota estornada */}
                    {n.estornada_em && (
                      <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-red-900/70 text-red-200">
                        estornada
                      </span>
                    )}
                  </span>
                  <span className="text-gray-400 text-xs whitespace-nowrap">
                    {fmtData(n.created_at)} · {n._count?.itens ?? n.itens?.length ?? 0} item(ns) ·{' '}
                    <span className="text-gray-200 font-medium">{fmtDinheiro(totalNota(n))}</span>
                  </span>
                </summary>
                {n.observacao && <p className="text-xs text-gray-400 mt-2">Obs: {n.observacao}</p>}
                {n.estornada_em && (
                  <p className="text-xs text-red-300 mt-2">
                    Estornada em {fmtData(n.estornada_em)}
                    {n.estornada_por_nome ? ` por ${n.estornada_por_nome}` : ''}
                    {n.estorno_motivo ? ` — motivo: ${n.estorno_motivo}` : ''}
                  </p>
                )}
                <div className="mt-3 space-y-1">
                  {(n.itens ?? []).map((i) => (
                    <div key={i.id} className="flex items-center justify-between text-xs bg-gray-900/60 rounded px-3 py-1.5">
                      <span className="text-gray-300">{i.produto?.name}</span>
                      <span className="text-gray-400">
                        {Number(i.quantidade)} {i.produto?.unidade || 'un'} × {fmtDinheiro(i.preco_unitario)} ={' '}
                        <span className="text-gray-200">{fmtDinheiro(Number(i.quantidade) * Number(i.preco_unitario))}</span>
                      </span>
                    </div>
                  ))}
                </div>
                {!n.estornada_em && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => void estornarNota(n)}
                      className="text-xs px-3 py-1 rounded-lg bg-red-900/50 hover:bg-red-900/70 text-red-200"
                      title="Devolve o estoque e desfaz o custo médio desta nota"
                    >
                      ⤺ Estornar nota
                    </button>
                  </div>
                )}
              </details>
            ))}

            <PaginationControls
              currentPage={notasPg.page}
              totalPages={notasPg.totalPages}
              totalItems={notasPg.totalItems}
              itemsPerPage={notasPg.limit}
              onPageChange={notasPg.setPage}
              onLimitChange={notasPg.setLimit}
              label="notas"
            />
          </div>
        </div>
      )}

      {/* ── TAB: INVENTÁRIO ───────────────────────────────────────────── */}
      {tab === 'inventario' && (
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-200">Contagem física</h3>
              <p className="text-xs text-gray-500">Digite o saldo contado de cada produto. Diferenças viram ajuste no histórico.</p>
            </div>
            <button
              onClick={() => aplicarInventario()}
              disabled={inventariando}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
            >
              {inventariando ? 'Aplicando...' : 'Aplicar inventário'}
            </button>
          </div>

          {/* T130: busca no servidor — com centenas de itens, nada fica escondido */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <input
              type="text"
              value={buscaEstoque}
              onChange={(e) => {
                setBuscaEstoque(e.target.value);
                setPaginaEstoque(1);
              }}
              placeholder="🔍 Buscar item com estoque controlado..."
              className="input-field sm:w-72"
            />
            <span className="text-xs text-gray-500">
              mostrando {controlados.length} de {totalEstoque} item(ns) com estoque controlado
            </span>
            {totalPaginasEstoque > 1 && (
              <div className="flex items-center gap-2 sm:ml-auto">
                <button
                  type="button"
                  onClick={() => setPaginaEstoque((p) => Math.max(1, p - 1))}
                  disabled={paginaEstoque <= 1}
                  className="px-3 py-1 rounded-lg text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-gray-200"
                >
                  ← Anterior
                </button>
                <span className="text-xs text-gray-400">
                  página {paginaEstoque} de {totalPaginasEstoque}
                </span>
                <button
                  type="button"
                  onClick={() => setPaginaEstoque((p) => Math.min(totalPaginasEstoque, p + 1))}
                  disabled={paginaEstoque >= totalPaginasEstoque}
                  className="px-3 py-1 rounded-lg text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-gray-200"
                >
                  Próxima →
                </button>
              </div>
            )}
          </div>
          {!isLoading && totalEstoque === 0 && (
            <p className="text-sm text-gray-400 py-4">
              {buscaEstoque.trim()
                ? `Nenhum item com estoque controlado encontrado para "${buscaEstoque.trim()}".`
                : 'Nenhum item com estoque controlado. Marque "controlar estoque" no cadastro do produto/insumo.'}
            </p>
          )}
          {/* Mobile: linhas viram cartões (sem rolagem lateral) */}
          <div className="md:hidden space-y-2">
            {controlados.map((p) => {
              const abaixo = p.estoque_atual <= p.estoque_minimo;
              return (
                <div key={p.id} className="bg-gray-900/60 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-200 font-medium truncate">{p.name}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${p.tipo === 'INSUMO' ? 'bg-purple-900/60 text-purple-200' : 'bg-blue-900/60 text-blue-200'}`}>
                      {p.tipo === 'INSUMO' ? 'Insumo' : 'Produto'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2 text-xs text-gray-400">
                    <span>
                      Saldo atual: <span className="text-gray-200 font-medium">{p.estoque_atual} {p.unidade || 'un'}</span> · Mínimo:{' '}
                      {p.estoque_minimo > 0 ? `${p.estoque_minimo} ${p.unidade || 'un'}` : '—'}
                    </span>
                    {abaixo && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-800/70 text-amber-200 whitespace-nowrap">repor</span>
                    )}
                  </div>
                  <div className="mt-2">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={contagem[p.id] ?? String(p.estoque_atual)}
                      onChange={(e) => setContagem((c) => ({ ...c, [p.id]: e.target.value }))}
                      className="input-field w-full text-right"
                      placeholder="Contado"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: tabela densa continua */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs border-b border-gray-700">
                  <th className="pb-2">Produto</th>
                  <th className="pb-2">Tipo</th>
                  <th className="pb-2 text-right">Saldo atual</th>
                  <th className="pb-2 text-right">Mínimo</th>
                  <th className="pb-2 text-right w-40">Contado</th>
                </tr>
              </thead>
              <tbody>
                {controlados.map((p) => {
                  const abaixo = p.estoque_atual <= p.estoque_minimo;
                  return (
                    <tr key={p.id} className="border-b border-gray-800/60">
                      <td className="py-2 text-gray-200">
                        {p.name}
                        {abaixo && (
                          <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-amber-800/70 text-amber-200">
                            repor
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.tipo === 'INSUMO' ? 'bg-purple-900/60 text-purple-200' : 'bg-blue-900/60 text-blue-200'}`}>
                          {p.tipo === 'INSUMO' ? 'Insumo' : 'Produto'}
                        </span>
                      </td>
                      <td className="py-2 text-right text-gray-300">
                        {p.estoque_atual} {p.unidade || 'un'}
                      </td>
                      <td className="py-2 text-right text-gray-400">
                        {p.estoque_minimo > 0 ? `${p.estoque_minimo} ${p.unidade || 'un'}` : '—'}
                      </td>
                      <td className="py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={contagem[p.id] ?? String(p.estoque_atual)}
                          onChange={(e) => setContagem((c) => ({ ...c, [p.id]: e.target.value }))}
                          className="input-field w-28 text-right"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAB: FORNECEDORES ─────────────────────────────────────────── */}
      {tab === 'fornecedores' && (
        <div className="bg-gray-800/50 rounded-lg p-4">
          {fornecedores.length === 0 && (
            <p className="text-gray-500 text-sm py-6 text-center">Nenhum fornecedor cadastrado.</p>
          )}
          {/* Mobile: cartões compactos */}
          <div className="md:hidden space-y-2">
            {fornecedores.map((f) => (
              <div key={f.id} className="bg-gray-900/60 rounded-lg p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-gray-200 font-medium truncate">{f.nome}</p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">
                    {f.telefone || '—'}
                    {f.cnpj ? ` · ${f.cnpj}` : ''}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{f._count?.notas ?? 0} nota(s) lançada(s)</p>
                </div>
                <button onClick={() => excluirFornecedor(f)} className="text-xs text-red-400 hover:text-red-300 whitespace-nowrap shrink-0">
                  Excluir
                </button>
              </div>
            ))}
          </div>

          {/* Desktop: tabela densa continua */}
          <table className="hidden md:table w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs border-b border-gray-700">
                <th className="pb-2">Nome</th>
                <th className="pb-2">CNPJ</th>
                <th className="pb-2">Telefone</th>
                <th className="pb-2 text-right">Notas</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {fornecedores.map((f) => (
                <tr key={f.id} className="border-b border-gray-800/60">
                  <td className="py-2 text-gray-200">{f.nome}</td>
                  <td className="py-2 text-gray-400">{f.cnpj || '—'}</td>
                  <td className="py-2 text-gray-400">{f.telefone || '—'}</td>
                  <td className="py-2 text-right text-gray-400">{f._count?.notas ?? 0}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => excluirFornecedor(f)} className="text-xs text-red-400 hover:text-red-300">
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MODAL: LANÇAR NOTA ────────────────────────────────────────── */}
      {showNota && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowNota(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-100 mb-4">🧾 Lançar nota de entrada</h2>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Fornecedor *</label>
                <div className="flex gap-2">
                  <select
                    value={notaForm.fornecedor_id}
                    onChange={(e) => setNotaForm((f) => ({ ...f, fornecedor_id: e.target.value }))}
                    className="input-field flex-1"
                  >
                    <option value="">Selecione...</option>
                    {fornecedores.map((f) => (
                      <option key={f.id} value={f.id}>{f.nome}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setMostrarNovoFornecedor((v) => !v)}
                    className="px-3 py-2 text-xs rounded-md bg-gray-700 hover:bg-gray-600 text-gray-200 whitespace-nowrap"
                  >
                    {mostrarNovoFornecedor ? 'Fechar' : '+ Novo'}
                  </button>
                </div>
                {mostrarNovoFornecedor && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 bg-gray-800/70 rounded-lg p-3">
                    <input
                      type="text" placeholder="Nome *" value={novoFornecedor.nome}
                      onChange={(e) => setNovoFornecedor((f) => ({ ...f, nome: e.target.value }))}
                      className="input-field col-span-1"
                    />
                    <input
                      type="text" placeholder="CNPJ" value={novoFornecedor.cnpj}
                      onChange={(e) => setNovoFornecedor((f) => ({ ...f, cnpj: e.target.value }))}
                      className="input-field"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text" placeholder="Telefone" value={novoFornecedor.telefone}
                        onChange={(e) => setNovoFornecedor((f) => ({ ...f, telefone: e.target.value }))}
                        className="input-field"
                      />
                      <button onClick={() => void criarFornecedorNaHora()} className="px-3 py-1 text-xs bg-emerald-700 hover:bg-emerald-600 text-white rounded-md">
                        OK
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text" placeholder="Número da nota (opcional)" value={notaForm.numero}
                  onChange={(e) => setNotaForm((f) => ({ ...f, numero: e.target.value }))}
                  className="input-field"
                />
                <input
                  type="text" placeholder="Observação (opcional)" value={notaForm.observacao}
                  onChange={(e) => setNotaForm((f) => ({ ...f, observacao: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>

            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-400">Itens da nota</label>
                <button onClick={() => adicionarItemNota()} className="text-xs text-primary-300 hover:text-primary-200 font-medium">
                  + Adicionar item
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                  <input
                    type="text"
                    value={buscaItemNota}
                    onChange={(e) => setBuscaItemNota(e.target.value)}
                    placeholder="🔍 Buscar item para a nota (nome do produto/insumo)"
                    className="input-field sm:w-80"
                  />
                  <span className="text-xs text-gray-500">
                    mostrando {opcoesSelectNota.length} item(ns) com estoque controlado
                  </span>
                </div>
                {notaForm.itens.length === 0 && (
                  <p className="text-xs text-gray-500 bg-gray-800/50 rounded-lg p-3">
                    Nenhum item ainda. Use a lista de compras sugerida (acima) ou adicione manualmente.
                  </p>
                )}
                {notaForm.itens.map((item, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <select
                      value={item.produto_id}
                      onChange={(e) => setNotaForm((f) => ({
                        ...f,
                        itens: f.itens.map((i, j) => (j === idx ? { ...i, produto_id: e.target.value } : i)),
                      }))}
                      className="input-field flex-1"
                    >
                      <option value="">Produto...</option>
                      {opcoesSelectNota.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.estoque_atual} {p.unidade || 'un'})
                        </option>
                      ))}
                    </select>
                    <div className="grid grid-cols-[1fr_1fr_auto] sm:grid-cols-[auto_auto_auto] sm:flex sm:items-center sm:gap-2 gap-2">
                    <input
                      type="number" min={1} step={1} placeholder="Qtd"
                      value={item.quantidade}
                      onChange={(e) => setNotaForm((f) => ({
                        ...f,
                        itens: f.itens.map((i, j) => (j === idx ? { ...i, quantidade: e.target.value } : i)),
                      }))}
                      className="input-field w-full sm:w-24"
                    />
                    <input
                      type="number" min={0} step="0.01" placeholder="R$ custo/un"
                      value={item.preco_unitario}
                      onChange={(e) => setNotaForm((f) => ({
                        ...f,
                        itens: f.itens.map((i, j) => (j === idx ? { ...i, preco_unitario: e.target.value } : i)),
                      }))}
                      className="input-field w-full sm:w-28"
                    />
                    <button
                      onClick={() => setNotaForm((f) => ({ ...f, itens: f.itens.filter((_, j) => j !== idx) }))}
                      className="text-red-400 hover:text-red-300 text-sm self-center"
                    >
                      ✕
                    </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {erro && <p className="text-sm text-red-400 mb-3">{erro}</p>}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowNota(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-200">
                Cancelar
              </button>
              <button
                onClick={() => void salvarNota()}
                disabled={salvando}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-500 text-white disabled:opacity-50"
              >
                {salvando ? 'Lançando...' : 'Lançar entrada'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: CADASTRAR FORNECEDOR ───────────────────────────────── */}
      {showFornecedor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowFornecedor(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-100 mb-4">🏢 Cadastrar fornecedor</h2>
            <div className="space-y-3">
              <input
                type="text" placeholder="Nome *" value={novoFornecedor.nome}
                onChange={(e) => setNovoFornecedor((f) => ({ ...f, nome: e.target.value }))}
                className="input-field"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text" placeholder="CNPJ" value={novoFornecedor.cnpj}
                  onChange={(e) => setNovoFornecedor((f) => ({ ...f, cnpj: e.target.value }))}
                  className="input-field"
                />
                <input
                  type="text" placeholder="Telefone" value={novoFornecedor.telefone}
                  onChange={(e) => setNovoFornecedor((f) => ({ ...f, telefone: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowFornecedor(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-200">
                Cancelar
              </button>
              <button
                onClick={() => void salvarFornecedor()}
                disabled={salvando}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-500 text-white disabled:opacity-50"
              >
                {salvando ? 'Salvando...' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mensagemEstoque && (
        <div
          className={`card mt-4 text-sm ${
            mensagemEstoque.tipo === 'ok' ? 'border-green-700/50 text-green-300' : 'border-red-700/50 text-red-300'
          }`}
        >
          {mensagemEstoque.linhas.map((l) => (
            <p key={l}>{l}</p>
          ))}
        </div>
      )}

      {/* T215: confirmações no modal do sistema */}
      {inventarioParaConfirmar && (
        <ModalConfirmacao
          titulo="Aplicar inventário"
          subtitulo={`${inventarioParaConfirmar.length} produto(s) com diferença`}
          resumo={[
            'O saldo passa a ser o valor contado.',
            'Cada ajuste entra no histórico com o seu usuário.',
          ]}
          rotuloConfirmar="Aplicar inventário"
          onConfirmar={confirmarInventario}
          onCancelar={() => setInventarioParaConfirmar(null)}
        />
      )}

      {fornecedorParaExcluir && (
        <ModalConfirmacao
          titulo="Excluir fornecedor"
          subtitulo={fornecedorParaExcluir.nome}
          resumo={['As notas de entrada já registradas não são apagadas.']}
          aviso="O fornecedor não aparece mais para novas compras."
          rotuloConfirmar="Excluir"
          onConfirmar={confirmarExclusaoFornecedor}
          onCancelar={() => setFornecedorParaExcluir(null)}
        />
      )}
    </div>
  );
}